import { Outlet, ScrollRestoration, useLocation, useNavigate, useRouter } from '@tanstack/react-router';
import { AnimatePresence } from 'framer-motion';
import React, { Suspense, useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import ConfirmDialog from '@/components/ConfirmDialog';
import LoadingCircular from '@/components/LoadingCircular';
import WeavrGuide from '@/features/guide/WeavrGuide';
import { getParticipationRoute } from '@/helpers/participationNavigation';
import { newDesignPaths } from '@/layouts/layout.constants';
import PageLayout from '@/layouts/oldLayout/PageLayout';
import SocialFeedLayout from '@/layouts/socialFeed/SocialFeedLayout';
import { checkRole, getAllowedRoles } from '@/routes';
import { useGlobalSettingsStore } from '@/store/useGlobalSettingsStore';

declare global {
  interface Window {
    zE?: (...args: any[]) => void;
  }
}

const RootLayout = () => {
  const [shouldShowLoading, setShouldShowLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { state } = useRouter();
  const { pathname } = useLocation();
  const { globalSettings, isAuthenticated, isAuthChecking } = useGlobalSettingsStore(
    useShallow(storeState => ({
      globalSettings: storeState.globalSettings,
      isAuthenticated: storeState.isAuthenticated,
      isAuthChecking: storeState.isAuthChecking
    }))
  );
  const isValidReturnUrl = (url: string) => {
    return url.startsWith('/') && !url.includes('://');
  };

  useEffect(() => {
    if (!window?.zE) return;

    window.zE('messenger:set', 'customization', {
      theme: {
        onPrimary: '#FFFFFF' // White text on buttons
      }
    });
  }, []);

  useEffect(() => {
    if (isAuthChecking) return;
    const searchParams = new URLSearchParams(window.location.search);
    const returnUrl = searchParams.get('returnUrl');

    if (isAuthenticated) {
      if (!globalSettings || !globalSettings.role || !globalSettings.organisationName) {
        setShouldShowLoading(true);
        return;
      } else {
        setShouldShowLoading(false);
        const allowedRoles = getAllowedRoles(location.pathname);
        if (!checkRole(globalSettings.role, allowedRoles)) {
          navigate({ to: '/not-authorized' });
          return;
        }
      }

      if (returnUrl && isValidReturnUrl(decodeURIComponent(returnUrl))) {
        navigate({ to: decodeURIComponent(returnUrl) });
      } else {
        if (location.pathname === '/workspace/manage/progress/editor') {
          navigate({ to: '/collaborate/edit', search: prev => ({ ...prev }) as any });
        }
        if (location.pathname === '/') {
          navigate({ to: getParticipationRoute(globalSettings.isSocialFeedEnabled) });
        }
      }
    } else {
      const publicPaths = ['/', '/terms', '/privacy_policy', '/security'];
      const isPublicPath =
        publicPaths.includes(window.location.pathname) ||
        location.pathname.startsWith('/casestudies') ||
        location.pathname.startsWith('/blogs') ||
        location.pathname.startsWith('/careers');

      if (!isPublicPath) {
        const currentPath = `${window.location.pathname}${window.location.search}`;
        const encodedReturnUrl = encodeURIComponent(currentPath);
        navigate({ to: '/', search: { returnUrl: encodedReturnUrl } });
      }
    }
  }, [isAuthChecking, isAuthenticated, navigate, globalSettings]);

  useEffect(() => {
    if (state.location.pathname.includes('//')) {
      const normalizedPathname = state.location.pathname.replace('//', '/');
      navigate({
        to: normalizedPathname,
        search: state.location.search,
        replace: true
      });
    }
  }, [state.location, navigate]);

  const isNewDesignPage = newDesignPaths.some(q => pathname.toLowerCase().startsWith(q));
  const LayoutComponent = isNewDesignPage ? SocialFeedLayout : PageLayout;

  useEffect(() => {
    if (!isNewDesignPage) {
      document.body.classList.add('bg-background');
      return;
    }

    document.body.classList.remove('bg-background');
  }, [isNewDesignPage]);

  return (
    <>
      <LayoutComponent>
        <Suspense fallback={<LoadingCircular />}>
          {shouldShowLoading ? (
            <LoadingCircular />
          ) : (
            <AnimatePresence mode='wait'>
              <div className='h-full'>
                <ScrollRestoration getKey={location => location.pathname} />
                <Outlet />
              </div>
            </AnimatePresence>
          )}
        </Suspense>
      </LayoutComponent>
      {isAuthenticated && globalSettings?.organisationName && <WeavrGuide />}
      <ConfirmDialog />
    </>
  );
};

export default RootLayout;
